/**
 * Centralized table settings for consistent configuration across all DataTables
 * 
 * This provides smart defaults for all table patterns while remaining fully customizable.
 * Supports internationalization through dynamic locale-aware settings.
 */

// Static base settings (fallbacks)
const BASE_TABLE_SETTINGS = {
  // Date formatting defaults
  dateFormat: {
    default: 'MMM dd, yyyy',
    withTime: 'MMM dd, yyyy HH:mm',
    short: 'MM/dd/yy',
    relative: true, // "2 days ago" vs "Jan 15, 2024"
  },
  
  // Currency formatting defaults
  currency: {
    default: 'USD',
    locale: 'en-US',
    precision: 2,
    showSymbol: true,
  },
  
  // Status display defaults
  status: {
    activeLabel: 'Active',
    inactiveLabel: 'Inactive',
    activeVariant: 'success',
    inactiveVariant: 'warning',
  },
  
  // Text formatting defaults
  text: {
    maxLength: 50,          // Truncate after 50 chars
    truncateStrategy: 'ellipsis',
    emptyDisplay: '-',
    linkColor: 'primary',
    className: 'font-medium',  // Good default styling for text columns
  },
  
  // Action button defaults
  actions: {
    size: 'sm',
    spacing: 'gap-2',
    confirmDestructive: true,
  },
  
  // Avatar display defaults
  avatar: {
    size: 'sm',
    fallbackType: 'initials',
    showTooltip: true,
  },
  
  // Number formatting defaults
  numbers: {
    locale: 'en-US',
    showThousandsSeparator: true,
    rightAlign: true,
    integerPrecision: 0,
    floatPrecision: 2,
    emptyDisplay: '-',
  },

  // Boolean column defaults
  boolean: {
    displayType: 'checkmark',
    checkmarkStyle: 'check',
    trueLabel: 'Yes',
    falseLabel: 'No',
    trueBadgeVariant: 'default',
    falseBadgeVariant: 'outline',
  },

  // Link column defaults
  links: {
    style: 'link',
    target: '_self',
    showIcon: false,
    truncateLength: 50,
    validateUrl: true,
    buttonVariant: 'outline',
    buttonSize: 'sm',
  },

  // Progress column defaults
  progress: {
    style: 'bar',
    colorScheme: 'default',
    showValue: true,
    showPercentage: false,
    lowThreshold: 33,
    mediumThreshold: 66,
    barHeight: 8,
    unit: '%',
  },

  // Rating column defaults
  rating: {
    style: 'stars',
    maxRating: 5,
    precision: 1,
    showValue: false,
    allowHalf: true,
    filledColor: '#FFD700',
    emptyColor: '#E5E7EB',
    size: 'md',
  },

  // Tags/chips column defaults
  tags: {
    style: 'badge',
    colorScheme: 'default',
    maxTags: 5,
    showCount: true,
    separator: ', ',
    sortTags: false,
    transform: 'none',
    size: 'sm',
    defaultColor: 'gray',
  },

  // Pagination defaults (extends existing smart pagination)
  pagination: {
    defaultPageSize: 10,
    pageSizeOptions: [10, 25, 50, 100],
    showPageInfo: true,
    showSizeSelector: true,
  }
};

// Locale-specific overrides and configurations
interface LocaleConfig {
  dateFormat?: {
    default?: string;
    withTime?: string;
    short?: string;
  };
  currency?: {
    default?: string;
    locale?: string;
  };
  numbers?: {
    locale?: string;
    showThousandsSeparator?: boolean;
  };
  boolean?: {
    trueLabel?: string;
    falseLabel?: string;
  };
  text?: {
    emptyDisplay?: string;
  };
}

// Predefined locale configurations
const LOCALE_CONFIGS: Record<string, LocaleConfig> = {
  'en-US': {
    dateFormat: {
      default: 'MMM dd, yyyy',
      withTime: 'MMM dd, yyyy hh:mm a',
      short: 'MM/dd/yy',
    },
    currency: { default: 'USD', locale: 'en-US' },
    numbers: { locale: 'en-US', showThousandsSeparator: true },
    boolean: { trueLabel: 'Yes', falseLabel: 'No' },
    text: { emptyDisplay: '-' },
  },
  
  'en-GB': {
    dateFormat: {
      default: 'dd MMM yyyy',
      withTime: 'dd MMM yyyy HH:mm',
      short: 'dd/MM/yy',
    },
    currency: { default: 'GBP', locale: 'en-GB' },
    numbers: { locale: 'en-GB', showThousandsSeparator: true },
    boolean: { trueLabel: 'Yes', falseLabel: 'No' },
    text: { emptyDisplay: '-' },
  },

  'de-DE': {
    dateFormat: {
      default: 'dd.MM.yyyy',
      withTime: 'dd.MM.yyyy HH:mm',
      short: 'dd.MM.yy',
    },
    currency: { default: 'EUR', locale: 'de-DE' },
    numbers: { locale: 'de-DE', showThousandsSeparator: true },
    boolean: { trueLabel: 'Ja', falseLabel: 'Nein' },
    text: { emptyDisplay: '-' },
  },

  'fr-FR': {
    dateFormat: {
      default: 'dd/MM/yyyy',
      withTime: 'dd/MM/yyyy HH:mm',
      short: 'dd/MM/yy',
    },
    currency: { default: 'EUR', locale: 'fr-FR' },
    numbers: { locale: 'fr-FR', showThousandsSeparator: true },
    boolean: { trueLabel: 'Oui', falseLabel: 'Non' },
    text: { emptyDisplay: '-' },
  },

  'es-ES': {
    dateFormat: {
      default: 'dd/MM/yyyy',
      withTime: 'dd/MM/yyyy HH:mm',
      short: 'dd/MM/yy',
    },
    currency: { default: 'EUR', locale: 'es-ES' },
    numbers: { locale: 'es-ES', showThousandsSeparator: true },
    boolean: { trueLabel: 'Sí', falseLabel: 'No' },
    text: { emptyDisplay: '-' },
  },

  'ja-JP': {
    dateFormat: {
      default: 'yyyy年MM月dd日',
      withTime: 'yyyy年MM月dd日 HH:mm',
      short: 'yy/MM/dd',
    },
    currency: { default: 'JPY', locale: 'ja-JP' },
    numbers: { locale: 'ja-JP', showThousandsSeparator: true },
    boolean: { trueLabel: 'はい', falseLabel: 'いいえ' },
    text: { emptyDisplay: '-' },
  },

  'zh-CN': {
    dateFormat: {
      default: 'yyyy年MM月dd日',
      withTime: 'yyyy年MM月dd日 HH:mm',
      short: 'yy/MM/dd',
    },
    currency: { default: 'CNY', locale: 'zh-CN' },
    numbers: { locale: 'zh-CN', showThousandsSeparator: true },
    boolean: { trueLabel: '是', falseLabel: '否' },
    text: { emptyDisplay: '-' },
  },
};

/**
 * Auto-detects user locale from browser or system
 */
function detectUserLocale(): string {
  // Check browser language
  if (typeof navigator !== 'undefined') {
    return navigator.language || navigator.languages?.[0] || 'en-US';
  }
  
  // Check Intl API
  if (typeof Intl !== 'undefined') {
    return Intl.DateTimeFormat().resolvedOptions().locale || 'en-US';
  }
  
  return 'en-US';
}

/**
 * Deep merges locale configuration with base settings
 */
function mergeSettings(base: typeof BASE_TABLE_SETTINGS, localeConfig: LocaleConfig) {
  return {
    ...base,
    dateFormat: { ...base.dateFormat, ...localeConfig.dateFormat },
    currency: { ...base.currency, ...localeConfig.currency },
    numbers: { ...base.numbers, ...localeConfig.numbers },
    boolean: { ...base.boolean, ...localeConfig.boolean },
    text: { ...base.text, ...localeConfig.text },
  };
}

/**
 * Generates locale-aware table settings
 */
export function getTableSettings(locale?: string) {
  const targetLocale = locale || detectUserLocale();
  
  // Try exact match first
  let localeConfig = LOCALE_CONFIGS[targetLocale];
  
  // Fallback to language code only (e.g., 'en' from 'en-AU')
  if (!localeConfig && targetLocale.includes('-')) {
    const languageCode = targetLocale.split('-')[0];
    const fallbackLocale = Object.keys(LOCALE_CONFIGS).find(key => key.startsWith(languageCode));
    if (fallbackLocale) {
      localeConfig = LOCALE_CONFIGS[fallbackLocale];
    }
  }
  
  // Final fallback to en-US
  if (!localeConfig) {
    localeConfig = LOCALE_CONFIGS['en-US'];
  }
  
  return mergeSettings(BASE_TABLE_SETTINGS, localeConfig);
}

/**
 * Reactive table settings that update when locale changes
 */
class ReactiveTableSettings {
  private currentLocale: string = detectUserLocale();
  private settings = getTableSettings(this.currentLocale);
  private listeners: (() => void)[] = [];

  get current() {
    return this.settings;
  }

  setLocale(locale: string) {
    if (locale !== this.currentLocale) {
      this.currentLocale = locale;
      this.settings = getTableSettings(locale);
      this.notifyListeners();
    }
  }

  subscribe(listener: () => void) {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  private notifyListeners() {
    this.listeners.forEach(listener => listener());
  }
}

// Singleton instance for app-wide settings
export const reactiveTableSettings = new ReactiveTableSettings();

// Static export for backward compatibility
export const TABLE_SETTINGS = BASE_TABLE_SETTINGS;

// Utility functions for i18n integration
export const TableSettingsI18n = {
  /**
   * For react-i18next integration
   */
  useWithI18next: (i18n: { language: string; on: (event: string, callback: () => void) => void }) => {
    const settings = getTableSettings(i18n.language);
    
    // Listen for language changes
    i18n.on('languageChanged', () => {
      reactiveTableSettings.setLocale(i18n.language);
    });
    
    return settings;
  },

  /**
   * For Next.js i18n integration
   */
  useWithNextI18n: (locale: string) => {
    return getTableSettings(locale);
  },

  /**
   * For custom i18n solutions
   */
  useWithCustomI18n: (getCurrentLocale: () => string, onLocaleChange: (callback: () => void) => void) => {
    const settings = getTableSettings(getCurrentLocale());
    
    onLocaleChange(() => {
      reactiveTableSettings.setLocale(getCurrentLocale());
    });
    
    return settings;
  },

  /**
   * Manual locale switching
   */
  setGlobalLocale: (locale: string) => {
    reactiveTableSettings.setLocale(locale);
  },

  /**
   * Get current reactive settings
   */
  getCurrent: () => reactiveTableSettings.current,

  /**
   * Subscribe to settings changes
   */
  subscribe: (callback: () => void) => reactiveTableSettings.subscribe(callback),
};

// Type extraction for TypeScript support
export type TableSettings = typeof BASE_TABLE_SETTINGS;
export type StatusVariant = typeof BASE_TABLE_SETTINGS.status.activeVariant;
export type ActionSize = typeof BASE_TABLE_SETTINGS.actions.size;
export type AvatarSize = typeof BASE_TABLE_SETTINGS.avatar.size;

// New column pattern types
export type BooleanDisplayType = typeof BASE_TABLE_SETTINGS.boolean.displayType;
export type CheckmarkStyle = typeof BASE_TABLE_SETTINGS.boolean.checkmarkStyle;
export type BadgeVariant = typeof BASE_TABLE_SETTINGS.boolean.trueBadgeVariant;
export type LinkStyle = typeof BASE_TABLE_SETTINGS.links.style;
export type LinkTarget = typeof BASE_TABLE_SETTINGS.links.target;
export type ButtonVariant = typeof BASE_TABLE_SETTINGS.links.buttonVariant;
export type ButtonSize = typeof BASE_TABLE_SETTINGS.links.buttonSize;
export type ProgressStyle = typeof BASE_TABLE_SETTINGS.progress.style;
export type ProgressColorScheme = typeof BASE_TABLE_SETTINGS.progress.colorScheme;
export type RatingStyle = typeof BASE_TABLE_SETTINGS.rating.style;
export type RatingSize = typeof BASE_TABLE_SETTINGS.rating.size;
export type TagStyle = typeof BASE_TABLE_SETTINGS.tags.style;
export type TagColorScheme = typeof BASE_TABLE_SETTINGS.tags.colorScheme;
export type TagSize = typeof BASE_TABLE_SETTINGS.tags.size;
export type TagTransform = typeof BASE_TABLE_SETTINGS.tags.transform; 